import React, { useState, useEffect } from 'react';
import { CalendarIcon, PlusIcon, CloudIcon } from '@heroicons/react/24/outline';
import api from '../../../services/api';
import { format, startOfMonth, endOfMonth, eachDayOfInterval, isSameMonth, isSameDay, isToday } from 'date-fns';

const CalendarWidget = () => {
  const [events, setEvents] = useState([]);
  const [currentDate, setCurrentDate] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showSyncModal, setShowSyncModal] = useState(false);

  useEffect(() => {
    fetchEvents();
  }, [currentDate]);

  const fetchEvents = async () => {
    try {
      const start = startOfMonth(currentDate);
      const end = endOfMonth(currentDate);
      const response = await api.get(
        `/portal/calendar/events?start_date=${start.toISOString()}&end_date=${end.toISOString()}`
      );
      setEvents(response.data);
    } catch (error) {
      console.error('Error fetching events:', error);
    } finally {
      setLoading(false);
    }
  };

  const getDaysInMonth = () => {
    const start = startOfMonth(currentDate);
    const end = endOfMonth(currentDate);
    return eachDayOfInterval({ start, end });
  };

  const getEventsForDate = (date) => {
    return events.filter(event => {
      const eventStart = new Date(event.start_time);
      return isSameDay(eventStart, date);
    });
  };

  const previousMonth = () => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() - 1));
  };

  const nextMonth = () => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() + 1));
  };

  const days = getDaysInMonth();
  const firstDayOfMonth = days[0].getDay();
  const daysOfWeek = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

  if (loading) {
    return (
      <div className="portal-widget h-full">
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="portal-widget h-full flex flex-col">
      <div className="portal-widget-header flex items-center justify-between">
        <h3 className="portal-widget-title flex items-center">
          <CalendarIcon className="h-5 w-5 mr-2" />
          Calendar
        </h3>
        <div className="flex items-center space-x-2">
          <button
            onClick={() => setShowSyncModal(true)}
            className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded"
            title="Sync with Outlook/Gmail"
          >
            <CloudIcon className="h-4 w-4" />
          </button>
          <button
            onClick={() => setShowCreateModal(true)}
            className="btn-primary px-3 py-1 text-sm flex items-center"
          >
            <PlusIcon className="h-4 w-4 mr-1" />
            Event
          </button>
        </div>
      </div>

      {/* Month Navigation */}
      <div className="flex items-center justify-between mb-4">
        <button
          onClick={previousMonth}
          className="px-3 py-1 theme-bg-secondary rounded hover:bg-gray-200 dark:hover:bg-gray-700"
        >
          ←
        </button>
        <h4 className="font-semibold theme-text-primary">
          {format(currentDate, 'MMMM yyyy')}
        </h4>
        <button
          onClick={nextMonth}
          className="px-3 py-1 theme-bg-secondary rounded hover:bg-gray-200 dark:hover:bg-gray-700"
        >
          →
        </button>
      </div>

      {/* Calendar Grid */}
      <div className="flex-1 overflow-y-auto">
        <div className="grid grid-cols-7 gap-1 mb-2">
          {daysOfWeek.map(day => (
            <div key={day} className="text-center text-xs font-semibold theme-text-secondary py-1">
              {day}
            </div>
          ))}
        </div>

        <div className="grid grid-cols-7 gap-1">
          {/* Empty cells for days before month starts */}
          {Array.from({ length: firstDayOfMonth }).map((_, index) => (
            <div key={`empty-${index}`} className="aspect-square" />
          ))}

          {/* Days of the month */}
          {days.map(day => {
            const dayEvents = getEventsForDate(day);
            const isSelected = isSameDay(day, selectedDate);
            const isCurrent = isToday(day);

            return (
              <button
                key={day.toISOString()}
                onClick={() => setSelectedDate(day)}
                className={`aspect-square p-1 rounded border theme-border-primary hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors relative ${
                  isSelected ? 'ring-2 ring-blue-500' : ''
                } ${isCurrent ? 'bg-blue-50 dark:bg-blue-900' : 'theme-bg-secondary'}`}
              >
                <div className={`text-sm ${isCurrent ? 'font-bold text-blue-600' : 'theme-text-primary'}`}>
                  {format(day, 'd')}
                </div>
                {dayEvents.length > 0 && (
                  <div className="absolute bottom-1 left-1/2 transform -translate-x-1/2 flex space-x-0.5">
                    {dayEvents.slice(0, 3).map((_, idx) => (
                      <div key={idx} className="w-1 h-1 rounded-full bg-blue-600" />
                    ))}
                  </div>
                )}
              </button>
            );
          })}
        </div>

        {/* Events for Selected Date */}
        {selectedDate && (
          <div className="mt-4">
            <h4 className="text-sm font-semibold theme-text-secondary mb-2">
              Events on {format(selectedDate, 'MMM dd, yyyy')}
            </h4>
            <div className="space-y-2 max-h-48 overflow-y-auto">
              {getEventsForDate(selectedDate).length === 0 ? (
                <p className="text-sm theme-text-tertiary">No events</p>
              ) : (
                getEventsForDate(selectedDate).map(event => (
                  <div key={event.id} className="theme-bg-tertiary rounded p-2">
                    <div className="font-medium theme-text-primary text-sm">{event.title}</div>
                    <div className="text-xs theme-text-secondary mt-1">
                      {format(new Date(event.start_time), 'h:mm a')} - {format(new Date(event.end_time), 'h:mm a')}
                    </div>
                    {event.location && (
                      <div className="text-xs theme-text-tertiary mt-1">📍 {event.location}</div>
                    )}
                  </div>
                ))
              )}
            </div>
          </div>
        )}
      </div>

      {showCreateModal && (
        <CreateEventModal
          onClose={() => setShowCreateModal(false)}
          onCreated={fetchEvents}
          initialDate={selectedDate}
        />
      )}

      {showSyncModal && (
        <CalendarSyncModal onClose={() => setShowSyncModal(false)} />
      )}
    </div>
  );
};

// Create Event Modal
const CreateEventModal = ({ onClose, onCreated, initialDate }) => {
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    start_time: format(initialDate || new Date(), "yyyy-MM-dd'T'HH:mm"),
    end_time: format(new Date((initialDate || new Date()).getTime() + 3600000), "yyyy-MM-dd'T'HH:mm"),
    location: '',
    event_type: 'meeting'
  });
  const [saving, setSaving] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);

    try {
      await api.post('/portal/calendar/events', formData);
      onCreated();
      onClose();
    } catch (error) {
      console.error('Error creating event:', error);
      alert('Failed to create event');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="modal-overlay">
      <div className="modal-container">
        <div className="modal-content max-w-2xl">
          <div className="modal-header">
            <h2 className="text-xl font-semibold theme-text-primary">Create Event</h2>
          </div>

          <form onSubmit={handleSubmit}>
            <div className="modal-body space-y-4">
              <div className="form-group">
                <label>Title *</label>
                <input
                  type="text"
                  required
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  className="form-input"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="form-group">
                  <label>Start Time *</label>
                  <input
                    type="datetime-local"
                    required
                    value={formData.start_time}
                    onChange={(e) => setFormData({ ...formData, start_time: e.target.value })}
                    className="form-input"
                  />
                </div>

                <div className="form-group">
                  <label>End Time *</label>
                  <input
                    type="datetime-local"
                    required
                    value={formData.end_time}
                    onChange={(e) => setFormData({ ...formData, end_time: e.target.value })}
                    className="form-input"
                  />
                </div>
              </div>

              <div className="form-group">
                <label>Location</label>
                <input
                  type="text"
                  value={formData.location}
                  onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                  className="form-input"
                />
              </div>

              <div className="form-group">
                <label>Description</label>
                <textarea
                  rows="3"
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  className="form-textarea"
                />
              </div>
            </div>

            <div className="modal-footer">
              <button type="button" onClick={onClose} className="btn-secondary" disabled={saving}>
                Cancel
              </button>
              <button type="submit" className="btn-primary" disabled={saving}>
                {saving ? 'Creating...' : 'Create Event'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

// Calendar Sync Modal
const CalendarSyncModal = ({ onClose }) => {
  return (
    <div className="modal-overlay">
      <div className="modal-container">
        <div className="modal-content max-w-md">
          <div className="modal-header">
            <h2 className="text-xl font-semibold theme-text-primary">Sync Calendar</h2>
          </div>

          <div className="modal-body space-y-4">
            <p className="theme-text-secondary">
              Connect your calendar to sync events with external providers.
            </p>

            <div className="space-y-3">
              <button
                onClick={() => alert('Outlook sync requires OAuth configuration')}
                className="w-full p-4 theme-bg-secondary rounded-lg hover:shadow-md transition-all flex items-center justify-between"
              >
                <div className="flex items-center space-x-3">
                  <div className="w-10 h-10 bg-blue-100 rounded flex items-center justify-center">
                    <span className="text-xl">📧</span>
                  </div>
                  <div className="text-left">
                    <div className="font-medium theme-text-primary">Microsoft Outlook</div>
                    <div className="text-sm theme-text-tertiary">Sync with Outlook Calendar</div>
                  </div>
                </div>
                <span className="text-sm text-blue-600">Connect →</span>
              </button>

              <button
                onClick={() => alert('Gmail sync requires OAuth configuration')}
                className="w-full p-4 theme-bg-secondary rounded-lg hover:shadow-md transition-all flex items-center justify-between"
              >
                <div className="flex items-center space-x-3">
                  <div className="w-10 h-10 bg-red-100 rounded flex items-center justify-center">
                    <span className="text-xl">📮</span>
                  </div>
                  <div className="text-left">
                    <div className="font-medium theme-text-primary">Google Calendar</div>
                    <div className="text-sm theme-text-tertiary">Sync with Gmail Calendar</div>
                  </div>
                </div>
                <span className="text-sm text-red-600">Connect →</span>
              </button>
            </div>

            <div className="p-3 bg-yellow-50 dark:bg-yellow-900 rounded-lg">
              <p className="text-sm text-yellow-800 dark:text-yellow-200">
                <strong>Note:</strong> Calendar sync requires OAuth credentials to be configured by your administrator.
              </p>
            </div>
          </div>

          <div className="modal-footer">
            <button onClick={onClose} className="btn-secondary">
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CalendarWidget;
